import React, { useEffect, useMemo, useRef, useState } from "react";
import axBaseEnumsMetaService from "../../services/meta/axBaseEnumsMetaService";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 1000;
const FLATTENED_VALUE_COLUMN = "__flattenedValue";

const stableStringify = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value !== "object") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${key}:${stableStringify(value[key])}`).join(",")}}`;
};

const normalizeFieldList = (fields) => {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields
    .filter((field) => typeof field === "string" && field.trim() !== "")
    .map((field) => field.trim());
};

const extractItems = (response) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.content)) {
    return response.content;
  }
  return [];
};

const normalizeValueKey = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const buildEnumLookupCandidates = (rawValue) => {
  const candidates = new Set();
  if (rawValue === undefined || rawValue === null) {
    return [];
  }

  const asString = String(rawValue);
  candidates.add(asString);

  const trimmed = asString.trim();
  if (trimmed) {
    candidates.add(trimmed);
  }

  const asNumber = Number(trimmed || asString);
  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
    candidates.add(String(asNumber));
    if (Number.isInteger(asNumber)) {
      candidates.add(String(parseInt(String(asNumber), 10)));
    }
  }

  return Array.from(candidates);
};

const getFieldCandidates = (fieldName) => {
  if (!fieldName || typeof fieldName !== "string") {
    return [];
  }

  const lower = fieldName.toLowerCase();
  const upper = fieldName.toUpperCase();
  const firstUpper = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  const firstLower = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
  const restLower =
    fieldName.length > 1
      ? fieldName.charAt(0).toLowerCase() + fieldName.slice(1).toLowerCase()
      : firstLower;

  return [...new Set([fieldName, lower, upper, firstUpper, firstLower, restLower])];
};

const getFieldValueLoose = (source, fieldName) => {
  if (!source || typeof source !== "object" || !fieldName) {
    return undefined;
  }

  if (fieldName.includes(".")) {
    const parts = fieldName.split(".").filter(Boolean);
    let current = source;
    for (const part of parts) {
      current = getFieldValueLoose(current, part);
      if (current === undefined || current === null) {
        return current;
      }
    }
    return current;
  }

  const candidates = getFieldCandidates(fieldName);
  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(source, candidate)) {
      return source[candidate];
    }
  }

  return undefined;
};

const joinDisplayFields = (item, fields, fallbackField) => {
  const safeItem = item || {};
  const effectiveFields = fields.length > 0 ? fields : [fallbackField];
  const values = effectiveFields
    .map((field) => getFieldValueLoose(safeItem, field))
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
    .map((value) => String(value));

  if (values.length === 0) {
    return "";
  }
  return values.join(" | ");
};

const mapItemsToLookupOptions = (items, config) => {
  const safeItems = Array.isArray(items) ? items : [];
  const displayFields = normalizeFieldList(config?.displayFields);
  const optionValue = config?.optionValue;
  const optionLabel = config?.optionLabel;
  const optionValueFields = normalizeFieldList(config?.optionValueFields);
  const selectedLabelField =
    typeof config?.selectedLabelField === "string" &&
    config.selectedLabelField.trim() !== ""
      ? config.selectedLabelField.trim()
      : null;

  // Flattened mode: instead of one option per row (keyed off a single
  // optionValue field), emit one option per configured field per row - e.g.
  // combining UnitConvert's TOUNIT and FROMUNIT columns into a single flat
  // list of selectable unit codes for an item. Caller dedupes the result.
  if (optionValueFields.length > 0) {
    return safeItems
      .flatMap((item) =>
        optionValueFields.map((field) => {
          const value = getFieldValueLoose(item, field);
          if (value === undefined || value === null || value === "") {
            return null;
          }
          return {
            value,
            label: String(value),
            item,
          };
        }),
      )
      .filter(Boolean);
  }

  return safeItems
    .map((item) => {
      const value = getFieldValueLoose(item, optionValue);
      if (value === undefined || value === null || value === "") {
        return null;
      }
      const displayLabel =
        (selectedLabelField
          ? getFieldValueLoose(item, selectedLabelField)
          : null) ||
        joinDisplayFields(item, displayFields, optionLabel) ||
        (getFieldValueLoose(item, optionLabel) !== undefined &&
        getFieldValueLoose(item, optionLabel) !== null
          ? String(getFieldValueLoose(item, optionLabel))
          : String(value));

      return {
        value,
        label: displayLabel,
        item,
      };
    })
    .filter(Boolean);
};

const dedupeLookupOptions = (options) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const seen = new Set();
  const deduped = [];
  safeOptions.forEach((option) => {
    const key = normalizeValueKey(option?.value);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    deduped.push(option);
  });
  return deduped;
};

export default function AdvancedLookupSelect({
  id,
  name,
  value,
  onValueChange,
  disabled = false,
  required = false,
  placeholder = "",
  ariaInvalid = false,
  dependencyFilters = {},
  staticOptions = [],
  lookupConfig = {},
  allowFreeText = false,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [activeSortField, setActiveSortField] = useState("");
  const [activeSortDirection, setActiveSortDirection] = useState("asc");
  const rootRef = useRef(null);
  const selectedRowRef = useRef(null);
  const debounceRef = useRef(null);
  const requestCounterRef = useRef(0);
  const [enumLabelsByField, setEnumLabelsByField] = useState({});

  const service = lookupConfig?.relatedService;
  const optionValue = lookupConfig?.optionValue;
  const optionLabel = lookupConfig?.optionLabel;
  const optionValueFields = normalizeFieldList(lookupConfig?.optionValueFields);
  const isFlattenedValueList = optionValueFields.length > 0;
  const displayFields = normalizeFieldList(lookupConfig?.displayFields);
  const searchFields = normalizeFieldList(lookupConfig?.searchFields);
  const searchModeRaw =
    typeof lookupConfig?.searchMode === "string"
      ? lookupConfig.searchMode.trim().toLowerCase()
      : "";
  const searchMode = searchModeRaw === "and" ? "and" : "or";
  const pageSize = Number(lookupConfig?.pageSize) > 0 ? Number(lookupConfig.pageSize) : DEFAULT_PAGE_SIZE;
  const enumFieldsConfig = lookupConfig?.enumFields;
  const valueLabelsConfig = lookupConfig?.valueLabels;
  const displayFieldLabelsConfig = lookupConfig?.displayFieldLabels;
  const sortConfig = lookupConfig?.sort;
  const dependencyFiltersConfig = dependencyFilters;
  const sortConfigKey = useMemo(() => stableStringify(sortConfig), [sortConfig]);
  const enumFields = useMemo(() => {
    const raw = enumFieldsConfig;
    if (!raw || typeof raw !== "object") {
      return {};
    }
    return raw;
  }, [enumFieldsConfig]);
  const valueLabels = useMemo(() => {
    const raw = valueLabelsConfig;
    if (!raw || typeof raw !== "object") {
      return {};
    }
    return raw;
  }, [valueLabelsConfig]);

  const displayFieldLabels = useMemo(() => {
    const raw = displayFieldLabelsConfig;
    if (!raw || typeof raw !== "object") {
      return {};
    }
    return raw;
  }, [displayFieldLabelsConfig]);
  const columnMaxWidthByField = useMemo(() => {
    const raw = lookupConfig?.columnMaxWidthByField;
    if (!raw || typeof raw !== "object") {
      return {};
    }
    return raw;
  }, [lookupConfig?.columnMaxWidthByField]);
  const defaultColumnMaxWidth = lookupConfig?.columnMaxWidth;

  useEffect(() => {
    let cancelled = false;

    const loadLookupEnumLabels = async () => {
      const entries = Object.entries(enumFields).filter(
        ([field, enumName]) =>
          typeof field === "string" &&
          field.trim() !== "" &&
          typeof enumName === "string" &&
          enumName.trim() !== "",
      );

      if (entries.length === 0) {
        setEnumLabelsByField({});
        return;
      }

      const resolved = {};
      await Promise.all(
        entries.map(async ([field, enumName]) => {
          const enumOptions = await axBaseEnumsMetaService.getOptions(enumName);
          const labelsMap = {};
          (Array.isArray(enumOptions) ? enumOptions : []).forEach((opt) => {
            const key = normalizeValueKey(opt?.value);
            if (!key) {
              return;
            }
            labelsMap[key] = opt?.label ?? String(opt?.value ?? "");
          });
          resolved[field.toLowerCase()] = labelsMap;
        }),
      );

      if (!cancelled) {
        setEnumLabelsByField(resolved);
      }
    };

    loadLookupEnumLabels();
    return () => {
      cancelled = true;
    };
  }, [enumFields]);

  const sortFieldFromConfig = lookupConfig?.sort?.field || lookupConfig?.optionValue || "";
  const sortDirectionFromConfig =
    String(lookupConfig?.sort?.direction || "asc").toLowerCase() === "desc"
      ? "desc"
      : "asc";

  useEffect(() => {
    const sortCfg = sortConfig;
    if (sortCfg && sortCfg.field) {
      setActiveSortField(sortCfg.field);
      setActiveSortDirection(sortDirectionFromConfig);
      return;
    }
    if (lookupConfig?.optionValue) {
      setActiveSortField(lookupConfig.optionValue);
      setActiveSortDirection("asc");
    }
  }, [lookupConfig?.optionValue, sortConfig, sortConfigKey, sortDirectionFromConfig, sortFieldFromConfig]);

  const sortArray = useMemo(() => {
    if (!activeSortField) {
      return [];
    }
    return [{ field: activeSortField, direction: activeSortDirection }];
  }, [activeSortField, activeSortDirection]);

  const dependencyFiltersKey = useMemo(
    () => stableStringify(dependencyFiltersConfig || {}),
    [dependencyFiltersConfig],
  );
  const mergedDependencyFilters = useMemo(
    () => ({ ...(dependencyFiltersConfig || {}) }),
    [dependencyFiltersConfig],
  );

  const fallbackOptions = useMemo(
    () =>
      (Array.isArray(staticOptions) ? staticOptions : []).map((opt) => ({
        value: opt?.value,
        label: opt?.label ?? String(opt?.value ?? ""),
        item: opt,
      })),
    [staticOptions],
  );

  const loadOptions = async (text) => {
    if (!service || !optionValue || !optionLabel) {
      setOptions(dedupeLookupOptions(fallbackOptions));
      setIsLoading(false);
      return;
    }

    const currentRequest = requestCounterRef.current + 1;
    requestCounterRef.current = currentRequest;
    setIsLoading(true);

    try {
      const trimmedText = typeof text === "string" ? text.trim() : "";
      let fetchedItems = [];

      const effectiveFilters = { ...mergedDependencyFilters };
      if (trimmedText !== "") {
        if (searchFields.length > 0) {
          effectiveFilters.lookupQuery = trimmedText;
          effectiveFilters.lookupSearchFields = searchFields;
          effectiveFilters.lookupSearchMode = searchMode;
        } else {
          const fallbackSearchField = optionLabel;
          if (fallbackSearchField) {
            effectiveFilters[fallbackSearchField] = trimmedText;
          }
        }
      }

      const response = await service.getAll(0, pageSize, sortArray, effectiveFilters);
      fetchedItems = extractItems(response);

      if (trimmedText !== "" && searchFields.length === 0) {
        const loweredQuery = trimmedText.toLowerCase();
        fetchedItems = fetchedItems.filter((item) => {
          const fallbackValue = getFieldValueLoose(item, optionLabel);
          if (fallbackValue === undefined || fallbackValue === null) {
            return false;
          }
          return String(fallbackValue).toLowerCase().includes(loweredQuery);
        });
      }

      const currentSelectedValue = normalizeValueKey(value);
      const hasSelectedValueInResults =
        currentSelectedValue &&
        fetchedItems.some(
          (item) =>
            normalizeValueKey(getFieldValueLoose(item, optionValue)) ===
            currentSelectedValue,
        );
      if (currentSelectedValue && !hasSelectedValueInResults) {
        try {
          const selectedResponse = await service.getAll(0, 1, sortArray, {
            ...mergedDependencyFilters,
            [optionValue]: currentSelectedValue,
          });
          const selectedItems = extractItems(selectedResponse);
          fetchedItems = [...selectedItems, ...fetchedItems];
        } catch (error) {
          // Ignore fallback fetch errors and keep current results.
        }
      }

      if (requestCounterRef.current !== currentRequest) {
        return;
      }

      const mapped = mapItemsToLookupOptions(fetchedItems, {
        optionValue,
        optionLabel,
        optionValueFields,
        displayFields,
        selectedLabelField: lookupConfig?.selectedLabelField,
      });
      setOptions(dedupeLookupOptions([...mapped, ...fallbackOptions]));
    } catch (error) {
      if (requestCounterRef.current !== currentRequest) {
        return;
      }
      setOptions(dedupeLookupOptions(fallbackOptions));
    } finally {
      if (requestCounterRef.current === currentRequest) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      loadOptions(query);
    }, 220);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, query, dependencyFiltersKey, sortArray, pageSize, optionLabel, optionValue, searchMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      return;
    }

    const safeValue = normalizeValueKey(value);
    const localMatch = options.find(
      (opt) => normalizeValueKey(opt?.value) === safeValue,
    );
    if (localMatch?.label) {
      setSelectedLabel(localMatch.label);
      return;
    }

    const staticMatch = fallbackOptions.find(
      (opt) => normalizeValueKey(opt?.value) === safeValue,
    );
    if (staticMatch?.label) {
      setSelectedLabel(staticMatch.label);
      return;
    }

    setSelectedLabel(safeValue);
  }, [value, options, fallbackOptions]);

  useEffect(() => {
    if (!isOpen || !selectedRowRef.current) {
      return;
    }
    selectedRowRef.current.scrollIntoView({ block: "nearest" });
  }, [isOpen, options, value]);

  // The click-outside listener below is registered once on mount, so it reads
  // through this ref (kept in sync every render) instead of closing over
  // stale allowFreeText/query/value/onValueChange from the mount render.
  const latestFreeTextStateRef = useRef(null);
  latestFreeTextStateRef.current = { allowFreeText, query, value, onValueChange };

  const commitFreeTextIfNeeded = () => {
    const { allowFreeText: canFreeType, query: currentQuery, value: currentValue, onValueChange: commit } =
      latestFreeTextStateRef.current;
    if (!canFreeType) {
      return;
    }
    const trimmedQuery = currentQuery.trim();
    if (trimmedQuery && trimmedQuery !== normalizeValueKey(currentValue)) {
      commit(trimmedQuery);
    }
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!rootRef.current || rootRef.current.contains(event.target)) {
        return;
      }
      commitFreeTextIfNeeded();
      setIsOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  const inputValue = isOpen ? (query !== "" ? query : selectedLabel) : selectedLabel;
  // In flattened mode (optionValueFields), each option's "item" is a whole
  // UnitConvert-style row and its other columns (e.g. factor) describe that
  // row's conversion, not the standalone unit being offered - showing them
  // next to a flattened value would misrepresent it. Render a single column
  // with just the option's own label/value instead of the per-row table.
  const columnsToRender = isFlattenedValueList
    ? [FLATTENED_VALUE_COLUMN]
    : displayFields.length > 0 ? displayFields : [optionValue, optionLabel].filter(Boolean);
  const placeholderText = placeholder || "Search...";
  const hasClearButton = Boolean(value) && !disabled;
  const inputPaddingRight = "30px";

  const renderLookupCellValue = (field, rawValue) => {
    if (rawValue === undefined || rawValue === null) {
      return "";
    }

    const fieldValueLabels = valueLabels[String(field || "").toLowerCase()] || valueLabels[field];
    if (fieldValueLabels && typeof fieldValueLabels === "object") {
      const candidates = buildEnumLookupCandidates(rawValue);
      for (const candidate of candidates) {
        const mappedLabel = fieldValueLabels[candidate];
        if (mappedLabel !== undefined && mappedLabel !== null && String(mappedLabel).trim() !== "") {
          return String(mappedLabel);
        }
      }
    }

    const fieldMap = enumLabelsByField[String(field || "").toLowerCase()];
    if (fieldMap && typeof fieldMap === "object") {
      const candidates = buildEnumLookupCandidates(rawValue);
      for (const candidate of candidates) {
        const mappedLabel = fieldMap[candidate];
        if (mappedLabel !== undefined && mappedLabel !== null && String(mappedLabel).trim() !== "") {
          return String(mappedLabel);
        }
      }
    }

    return String(rawValue);
  };

  const resolveLookupHeaderLabel = (field) => {
    const directLabel =
      displayFieldLabels[field] ??
      displayFieldLabels[String(field || "").toLowerCase()];

    if (typeof directLabel === "string" && directLabel.trim() !== "") {
      if (directLabel.includes(".")) {
        return t(directLabel);
      }
      return directLabel;
    }

    if (typeof field !== "string" || !field.trim()) {
      return "";
    }

    const spaced = field
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .trim();

    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const resolveLookupColumnStyle = (field) => {
    const specific =
      columnMaxWidthByField[field] ??
      columnMaxWidthByField[String(field || "").toLowerCase()];
    const maxWidthValue = specific ?? defaultColumnMaxWidth;
    if (!maxWidthValue) {
      return { whiteSpace: "nowrap" };
    }
    const normalizedWidth =
      typeof maxWidthValue === "number" ? `${maxWidthValue}px` : String(maxWidthValue);
    return {
      whiteSpace: "nowrap",
      maxWidth: normalizedWidth,
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }}>
        <input
          id={id}
          name={name}
          type="text"
          className={`form-control ${ariaInvalid ? "has-error" : ""}`}
          style={{ paddingRight: inputPaddingRight }}
          value={inputValue}
          placeholder={placeholderText}
          onFocus={() => {
            setIsOpen(true);
          }}
          onChange={(event) => {
            setIsOpen(true);
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitFreeTextIfNeeded();
              setIsOpen(false);
              setQuery("");
            } else if (event.key === "Escape") {
              setIsOpen(false);
              setQuery("");
            }
          }}
          disabled={disabled}
          required={required}
          aria-required={required}
          aria-invalid={ariaInvalid}
          autoComplete="off"
        />
        <button
          type="button"
          className="btn btn-default"
          style={{
            position: "absolute",
            right: "4px",
            top: "50%",
            transform: "translateY(-50%)",
            border: "none",
            background: "transparent",
            boxShadow: "none",
            padding: "0 2px",
            height: "20px",
            lineHeight: "20px",
            minWidth: "auto",
          }}
          onClick={() => {
            if (disabled) {
              return;
            }
            setIsOpen((prev) => !prev);
          }}
          aria-label={isOpen ? "Close lookup" : "Open lookup"}
          disabled={disabled}
        >
          <i className="fa fa-chevron-down" />
        </button>
        </div>
        {hasClearButton && (
          <button
            type="button"
            className="btn btn-default"
            onClick={() => {
              onValueChange("");
              setQuery("");
              setSelectedLabel("");
            }}
            aria-label="Clear lookup value"
          >
            <i className="fa fa-times" />
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <div
          className="idax-lookup-dropdown"
          style={{
            position: "absolute",
            zIndex: 30,
            width: "auto",
            minWidth: "560px",
            maxWidth: "90vw",
            marginTop: "4px",
            maxHeight: "280px",
            overflow: "auto",
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
          }}
        >
          {isLoading ? (
            <div style={{ padding: "10px 12px" }}>Loading...</div>
          ) : options.length === 0 ? (
            <div style={{ padding: "10px 12px" }}>No results</div>
          ) : (
            <table
              className="table table-condensed mb-none idax-lookup-table"
              style={{ width: "max-content", minWidth: "100%", tableLayout: "auto" }}
            >
              <thead>
                <tr>
                  {columnsToRender.map((field) => (
                    <th
                      key={field}
                      style={
                        isFlattenedValueList
                          ? resolveLookupColumnStyle(field)
                          : { ...resolveLookupColumnStyle(field), cursor: "pointer" }
                      }
                      onClick={
                        isFlattenedValueList
                          ? undefined
                          : () => {
                              if (activeSortField === field) {
                                setActiveSortDirection((prev) =>
                                  prev === "asc" ? "desc" : "asc",
                                );
                                return;
                              }
                              setActiveSortField(field);
                              setActiveSortDirection("asc");
                            }
                      }
                    >
                      {isFlattenedValueList
                        ? resolveLookupHeaderLabel(optionLabel)
                        : resolveLookupHeaderLabel(field)}
                      {!isFlattenedValueList && activeSortField === field
                        ? activeSortDirection === "asc"
                          ? " ^"
                          : " v"
                        : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {options.map((option) => {
                  const isSelected =
                    normalizeValueKey(option?.value) === normalizeValueKey(value);
                  return (
                  <tr
                    key={normalizeValueKey(option.value)}
                    ref={isSelected ? selectedRowRef : null}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#d9ecff" : "transparent",
                    }}
                    className={isSelected ? "active" : ""}
                    onClick={() => {
                      onValueChange(option.value);
                      setSelectedLabel(option.label);
                      setQuery(option.label);
                      setIsOpen(false);
                    }}
                  >
                    {columnsToRender.map((field) => (
                      <td
                        key={`${normalizeValueKey(option.value)}-${field}`}
                        style={resolveLookupColumnStyle(field)}
                      >
                        {field === FLATTENED_VALUE_COLUMN
                          ? option.label
                          : (() => {
                              const cellValue = getFieldValueLoose(option?.item, field);
                              return renderLookupCellValue(field, cellValue);
                            })()}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

